import { ILesson } from "@/types/schedules";
import { API_URL } from "@/services/constants";
import { format, differenceInCalendarDays, isSameDay } from "date-fns";

// Formate the time remaining
export const formatDateString = (date: Date): string => {
  const formatter = new Intl.DateTimeFormat("en-GB", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  return formatter.format(date);
};

export function formatStringtoDate(dateString: string): string {
  const date = new Date(dateString);
  const day = date.getUTCDate();
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const month = monthNames[date.getUTCMonth()];
  const year = date.getUTCFullYear();

  return `${day} ${month} ${year}`;
}

type TimeSlot = `${string}${"am" | "pm"}`;

export const formatTimeSlot = (time: Date): TimeSlot => {
  const h = time.getHours();
  const hour = (h === 12 ? 12 : h % 12).toString().padStart(2, "0");
  const minute = time.getMinutes().toString().padStart(2, "0");
  const meridian = h >= 12 ? "pm" : "am";
  return `${hour}:${minute}${meridian}`;
};

export function isLessonInProgress<T extends { time: ILesson["time_slot"] }>(
  lesson: T,
): boolean {
  const { time } = lesson;

  const startTime = new Date(time.start);
  const endTime = new Date(time.end);
  const now = new Date();

  return now >= startTime && now <= endTime;
}

export function isLessonInPast<T extends { time: ILesson["time_slot"] }>(
  lesson: T,
): boolean {
  const { time } = lesson;

  const endTime = new Date(time.end);

  const now = new Date();

  return now > endTime;
}

export const timeDifference = (targetDate: string): string => {
  const now: Date = new Date();
  const target: Date = new Date(targetDate);

  // Calculate the difference in milliseconds
  const differenceInMs: number = target.getTime() - now.getTime();

  // If the difference is negative or zero, the target date has passed or is now
  if (differenceInMs <= 0) {
    return "";
  }

  // Convert milliseconds to total seconds
  const differenceInSeconds: number = differenceInMs / 1000;

  // Calculate the remaining days, hours, minutes, and seconds
  const days: number = Math.floor(differenceInSeconds / 86400);
  const hours: number = Math.floor((differenceInSeconds % 86400) / 3600);
  const mins: number = Math.floor((differenceInSeconds % 3600) / 60);
  const secs: number = Math.floor(differenceInSeconds % 60);

  // Return formatted time
  if (days > 0) {
    return `${days} day(s)`;
  } else if (hours > 0) {
    return `${hours} hour(s) ${mins} min(s)`;
  } else if (mins > 0) {
    return `${mins} min(s) ${secs} sec(s)`;
  } else {
    return `${secs} sec(s)`;
  }
};

export const removeMultipleKeys = (keys: string[]): void => {
  keys.forEach((key) => {
    localStorage.removeItem(key);
  });
};

export function parseJSON<T>(value: string | null): T | null {
  try {
    return value ? (JSON.parse(value) as T) : null;
  } catch {
    return null;
  }
}

export const isMoreThan48HoursAgo = (time: Date | string): boolean => {
  const now = new Date();
  const givenTime = typeof time === "string" ? new Date(time) : time;
  const fortyEightHoursAgo = new Date(now.getTime() - 48 * 60 * 60 * 1000);

  return givenTime < fortyEightHoursAgo;
};
export function debounce<T extends (...args: any[]) => void>(
  func: T,
  delay: number,
): T {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  return function (...args: Parameters<T>) {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      func(...args);
    }, delay);
  } as T;
}

export const camelToSnakeCase = (str: string) =>
  str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

export const currencyFormat = (
  number: number,
  locale: "en-US" = "en-US",
  currency = "USD",
) => {
  return new Intl.NumberFormat(locale, { style: "currency", currency }).format(
    number,
  );
};
interface CheckBalanceResult {
  result: boolean;
  possibleAmountToAdd?: number;
}

export const checkBalance = (
  userBalance: number,
  amount: number,
): CheckBalanceResult => {
  const total = userBalance + amount;

  if (total > 500) {
    return {
      result: true,
      possibleAmountToAdd: 500 - userBalance,
    };
  } else {
    return {
      result: false,
    };
  }
};

interface ScheduleSlot {
  start: string; // ISO string or Date string
  end: string; // ISO string or Date string
}

interface SelectedSlot {
  date: string; // ISO string or Date string
  time: string; // Time slot like "9:00am to 9:30am"
}

interface TimeSlots {
  value: string;
  label: string;
}

interface ScheduleSlot {
  start: string;
  end: string;
}

interface SelectedSlot {
  date: string; // ISO string or Date string
  time: string; // Time slot like "9:00am to 9:30am"
}

interface TimeSlots {
  value: string;
  label: string;
}

export const generateTimeSlots = (
  schedule: ScheduleSlot[],
  intervalMinutes: number,
  selectedSlots: SelectedSlot[],
  day: Date | null,
): TimeSlots[] => {
  const timeSlots: TimeSlots[] = [];
  if (!day) return timeSlots;

  const formatTime = (hour: any, minute: any) => {
    const ampm = hour >= 12 ? "pm" : "am";
    const formattedHour = hour % 12 === 0 ? 12 : hour % 12;
    const formattedMinute = minute.toString().padStart(2, "0");
    return `${formattedHour}:${formattedMinute}${ampm}`;
  };

  const today = new Date();
  const isToday = today.toDateString() === day?.toDateString();

  // Helper: round up a Date to the next interval boundary (in minutes)
  const roundUpToInterval = (d: Date, intervalMin: number) => {
    const rounded = new Date(d);
    rounded.setSeconds(0, 0);
    const minsSinceMidnight = rounded.getHours() * 60 + rounded.getMinutes();
    const remainder = minsSinceMidnight % intervalMin;
    if (remainder === 0) return rounded;
    const addMins = intervalMin - remainder;
    return new Date(rounded.getTime() + addMins * 60000);
  };

  schedule?.forEach((slot) => {
    const start = new Date(slot.start);
    const end = new Date(slot.end);

    const sameDay = start.toDateString() === day.toDateString();
    const overlapsNextDay =
      start < new Date(day) &&
      end > new Date(new Date(day).setHours(0, 0, 0, 0));

    if (sameDay || overlapsNextDay) {
      const adjustedStart = new Date(
        Math.max(start.getTime(), new Date(day).setHours(0, 0, 0, 0)),
      );
      const adjustedEnd = new Date(end.getTime());

      let currentDateTime: Date;
      if (isToday) {
        const nowRoundedUp = roundUpToInterval(new Date(), intervalMinutes);
        // ensure we don't start before the slot's adjustedStart
        currentDateTime = new Date(
          Math.max(nowRoundedUp.getTime(), adjustedStart.getTime()),
        );
      } else {
        currentDateTime = adjustedStart;
      }

      // if rounded start is already >= adjustedEnd, skip this slot
      if (currentDateTime >= adjustedEnd) return;

      while (currentDateTime < adjustedEnd) {
        const nextTime = new Date(
          currentDateTime.getTime() + intervalMinutes * 60000,
        );
        if (nextTime > adjustedEnd) break;

        const startTime = formatTime(
          currentDateTime.getHours(),
          currentDateTime.getMinutes(),
        );
        const endTime = formatTime(nextTime.getHours(), nextTime.getMinutes());

        const dayDiff = differenceInCalendarDays(nextTime, new Date(day));
        let extraLabel = "";
        if (dayDiff === 1) extraLabel = ` ( ${format(nextTime, "MMM d")})`;
        else if (dayDiff > 1) extraLabel = ` ( ${format(nextTime, "MMM d")})`;

        const timeSlot = `${startTime} to ${endTime}${extraLabel}`;

        const isSlotSelected = selectedSlots.some(
          (selectedSlot) =>
            new Date(selectedSlot.date).toDateString() ===
              start.toDateString() && selectedSlot.time === timeSlot,
        );

        if (!isSlotSelected) {
          timeSlots.push({
            value: timeSlot,
            label: ` ${timeSlot}`,
          });
        }

        currentDateTime = nextTime;
      }
    }
  });

  return timeSlots;
};

export const generateTimeSlotsForDates = (
  dates: { start: string; end: string; id?: string }[],
  day: string | Date,
) => {
  if (!day) return [];
  const target = new Date(day);

  return dates
    .filter((d) => isSameDay(new Date(d.start), target))
    .map((d) => {
      const startDate = new Date(d.start);
      const endDate = new Date(d.end);

      const dayDiff = differenceInCalendarDays(endDate, startDate);

      let endLabel = format(endDate, "h:mm a").toLowerCase();

      // If end date goes to the next day or more
      if (dayDiff >= 1) {
        const nextDayLabel =
          dayDiff === 1
            ? "(ends next day)"
            : `(ends ${format(endDate, "MMM d")})`;
        endLabel += ` ${nextDayLabel}`;
      }

      return {
        id: d.id ?? `${d.start}_${d.end}`,
        startTime: format(startDate, "h:mm a").toLowerCase(),
        endTime: endLabel,
      };
    });
};

export const formatDate = (date: Date): string => {
  const day = date.getDate();
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();

  const ordinalSuffix = (day: number): string => {
    if (day > 3 && day < 21) return "th";
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  return `${month}. ${day}${ordinalSuffix(day)}, ${year}`;
};

export const isTimeOver24HoursAgo = (timestamp: string | Date): boolean => {
  const currentTime = new Date();
  const providedTime = new Date(timestamp);

  // Ensure the provided timestamp is a valid date
  if (isNaN(providedTime.getTime())) {
    throw new Error("Invalid timestamp provided");
  }

  const timeDifference = currentTime.getTime() - providedTime.getTime();
  const hoursDifference = timeDifference / (1000 * 60 * 60);

  return hoursDifference >= 24;
};

export const checkEqual = (name1: string, name2: string): boolean => {
  const formatName = (name: string) =>
    name
      .toLowerCase() // Convert to lowercase
      .replace(/[^a-z]/g, ""); // Remove non-alphabetic characters

  const formattedName1 = formatName(name1);
  const formattedName2 = formatName(name2);

  return formattedName1 === formattedName2;
};

export interface DateTimeInput {
  date: string;
  time: string;
}

export interface ConvertedDateTime {
  start: string;
  end: string;
}

export const convertDateTimeArray = (
  inputArray: DateTimeInput[],
): ConvertedDateTime[] => {
  return inputArray.map(({ date, time }) => {
    if (!date || !time) {
      throw new Error("Invalid input: date and time are required.");
    }

    const [startTime, endTime] = time.split(" to ");
    if (!startTime || !endTime) {
      throw new Error(
        `Invalid time format: "${time}". Expected format: "HH:MMam/pm to HH:MMam/pm".`,
      );
    }

    // Convert time to 24-hour format
    const convertTo24Hour = (timeStr: string): string => {
      const match = timeStr.match(/^(\d{1,2}):(\d{2})(am|pm)$/i);
      if (!match) {
        throw new Error(
          `Invalid time format: "${timeStr}". Expected format: "HH:MMam/pm".`,
        );
      }

      let [hours, minutes, modifier] = match.slice(1);
      let hour = parseInt(hours, 10);

      if (modifier.toLowerCase() === "pm" && hour !== 12) {
        hour += 12;
      }
      if (modifier.toLowerCase() === "am" && hour === 12) {
        hour = 0;
      }

      return `${hour.toString().padStart(2, "0")}:${minutes}:00`;
    };

    const dateString = new Date(date).toISOString().split("T")[0];

    const startDateTime = new Date(
      `${dateString}T${convertTo24Hour(startTime)}`,
    );
    const endDateTime = new Date(`${dateString}T${convertTo24Hour(endTime)}`);

    // Adjust time by adding one day (24 hours in milliseconds)
    const adjustedStart = new Date(
      startDateTime.getTime() + 24 * 60 * 60 * 1000,
    );
    const adjustedEnd = new Date(endDateTime.getTime() + 24 * 60 * 60 * 1000);

    return {
      start: adjustedStart.toISOString(),
      end: adjustedEnd.toISOString(),
    };
  });
};

export const formatGameTimeStamp = (seconds: number) => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.round(seconds % 60);

  if (hrs > 0) return `${hrs} : ${mins} : ${secs} sec`;
  if (mins > 0) return `${mins}:${secs} sec`;
  return `${secs}sec`;
};
export const formatTimeStamp = (seconds: number) => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.round(seconds % 60);

  return `${hrs} : ${mins} : ${secs}`;
};

export const formatChatTime = (dateString: any) => {
  const date = new Date(dateString);
  const now = new Date();

  const diffMs = now.getTime() - date.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);

  const isToday = date.toDateString() === now.toDateString();

  if (isToday) {
    return date.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } else if (diffDays < 2) {
    return "Yesterday";
  } else if (diffDays <= 7) {
    return date.toLocaleDateString("en-GB", { weekday: "long" });
  } else {
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  }
};

export const formatExpiryDate = (dateString: Date) => {
  const date = new Date(dateString);

  const day = date.getDate();

  const getOrdinal = (n: number): string => {
    if (n > 3 && n < 21) return "th";
    switch (n % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  const dayWithOrdinal = `${day}${getOrdinal(day)}`;

  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month = monthNames[date.getMonth()];

  const year = date.getFullYear();

  return `${dayWithOrdinal} of ${month} ${year}`;
};

export const leaderboardFormatTimeLeft = (targetDate: any) => {
  const now = new Date();
  const target = new Date(targetDate);
  const diff = Number(target) - Number(now);

  if (diff <= 0) return "Expired";

  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    return `${days} DAY${days > 1 ? "S" : ""} LEFT`;
  } else if (hours > 0) {
    return `${hours} HOUR${hours > 1 ? "S" : ""} LEFT`;
  } else if (minutes > 0) {
    return `${minutes} MINUTE${minutes > 1 ? "S" : ""} LEFT`;
  } else {
    return `${seconds} SECOND${seconds > 1 ? "S" : ""} LEFT`;
  }
};
